import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { MDBDataTable } from 'mdbreact';
import { gql } from 'apollo-boost';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  Row,
  Col,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';

import { activateAuthLayout } from '../../../../store/actions';
import SweetAlert from 'react-bootstrap-sweetalert';

const GET_AGENTS = gql`
  query {
    Agents {
      AgentId
      OctaneId
      Name
    }
  }
`;

const CREATE_AGENT = gql`
  mutation createAgent($AgentInput: AgentInput) {
    createAgent(input: $AgentInput) {
      AgentId
      Name
    }
  }
`;

const UPDATE_AGENT = gql`
  mutation updateAgent($AgentInput: AgentInput) {
    updateAgent(input: $AgentInput) {
      AgentId
      OctaneId
      Name
    }
  }
`;

const DELETE_AGENT = gql`
  mutation deleteAgent($AgentId: ID!) {
    deleteAgent(AgentId: $AgentId) {
      AgentId
      Name
    }
  }
`;

const ManageAgents = ({ activateAuthLayout }) => {
  const { data, loading, refetch } = useQuery(GET_AGENTS);
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: [
          'Octane ID',
          <i key={`octane_id`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'octane_id',
        sort: 'asc',
      },
      {
        label: [
          'Agent Name',
          <i key={`agent_name`} className="fa fa-sort ml-2"></i>,
        ],
        field: 'agent_name',
        sort: 'asc',
      },
      {
        label: 'Action',
        field: 'actions',
        sort: 'disabled',
      },
    ],
  });

  const [formModal, setFormModal] = useState(false);
  const [formInput, setFormInput] = useState(null);
  const [formType, setFormType] = useState('create');

  const [createAgent] = useMutation(CREATE_AGENT);
  const [updateAgent] = useMutation(UPDATE_AGENT);
  const [deleteAgent] = useMutation(DELETE_AGENT);

  const [mutateResult, setMutateResult] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const showFormModal = useCallback((agent = null) => {
    setFormModal(prev => !prev);
    setFormInput(agent);
    setFormType(agent ? 'edit' : 'create');
  }, []);

  const deleteSelected = useCallback((agent) => {
    deleteAgent({ variables: { AgentId: agent.AgentId } }).then(() => {
      refetch();
      setMutateResult({ title: 'Success', message: 'Agent deleted successfully!', type: 'success', showConfirm: false });
    }).catch((err) => {
      setMutateResult({ title: 'Error', message: err.graphQLErrors?.[0]?.message || 'Failed to delete agent.', type: 'danger', showConfirm: true });
    });
  }, [deleteAgent, refetch]);

  useEffect(() => {
    activateAuthLayout();
    refetch();
  }, [activateAuthLayout, refetch]);

  useEffect(() => {
    if (!loading && data) {
      const rows = data.Agents.map((agent) => {
        return {
          octane_id: agent.OctaneId,
          agent_name: agent.Name,
          actions: (
            <div>
              <Button
                color="info"
                size="sm"
                className="mr-2"
                onClick={() => showFormModal(agent)}
              >
                Edit
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={() => setDeleteTarget(agent)}
              >
                Delete
              </Button>
            </div>
          ),
        };
      });
      setTableData(prev => ({ ...prev, rows }));
    }
  }, [data, loading, deleteSelected, showFormModal]);

  const updateForm = (field, value) => {
    setFormInput(prev => ({ ...prev, [field]: value }));
  };

  const save = (e) => {
    e.preventDefault();
    if (formType === 'edit') {
      const agent = {
        AgentId: formInput?.AgentId || null,
        OctaneId: formInput?.OctaneId || null,
        Name: formInput?.Name || '',
      };
      updateAgent({ variables: { AgentInput: agent } }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Successfully updated agent', type: 'success', showConfirm: false });
        setFormInput(null);
        setFormModal(false);
      });
    } else {
      const agent = {
        OctaneId: formInput?.OctaneId || null,
        Name: formInput?.Name || '',
      };
      createAgent({ variables: { AgentInput: agent } }).then(() => {
        refetch();
        setMutateResult({ title: 'Success', message: 'Successfully created agent', type: 'success', showConfirm: false });
        setFormInput(null);
        setFormModal(false);
      });
    }
  };

  return (
    <React.Fragment>
      {deleteTarget && (
        <SweetAlert
          warning
          title="Confirm"
          onConfirm={() => { deleteSelected(deleteTarget); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
          showCancel
          confirmBtnText="Delete"
          cancelBtnText="Cancel"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="secondary"
        >
          Delete agent "{deleteTarget.Name}"?
        </SweetAlert>
      )}
      <div className="content">
        <div className="container-fluid">
          <div className="page-title-box">
            <Row className="align-items-center">
              <Col sm="6">
                <h4 className="page-title">Referring Agents</h4>
              </Col>
              <Col sm="6" className="text-right">
                <Button color="success" onClick={() => showFormModal()}>Create New Agent</Button>
              </Col>
            </Row>
          </div>
          <div className="rounded bg-white p-4">
            {!loading && tableData.rows && (
              <MDBDataTable
                sortable
                striped
                borderless
                theadColor="table-dark"
                hover
                responsive
                noBottomColumns
                noRecordsFoundLabel="No Agents Found"
                data={tableData}
              />
            )}
          </div>
        </div>
        <Modal isOpen={formModal} toggle={() => showFormModal()}>
          <ModalHeader toggle={() => showFormModal()}>
            {formType === 'edit' ? 'Edit Agent' : 'Create New Agent'}
          </ModalHeader>

          <ModalBody>
            <Form onSubmit={save}>
              <FormGroup>
                <Label>Octane ID <small className="text-danger">*</small></Label>
                <Input
                  type="text"
                  placeholder="Octane ID"
                  defaultValue={formInput?.OctaneId || ''}
                  onChange={(e) => updateForm('OctaneId', e.target.value)}
                />
              </FormGroup>
              <FormGroup>
                <Label>
                  Agent Name <small className="text-danger">*</small>
                </Label>
                <Input
                  type="text"
                  placeholder="Agent Name"
                  defaultValue={formInput?.Name || ''}
                  onChange={(e) => updateForm('Name', e.target.value)}
                />
              </FormGroup>
            </Form>
          </ModalBody>

          <ModalFooter>
            <Button color="secondary" onClick={() => showFormModal()}>Cancel</Button>
            <Button
              color="primary"
              onClick={save}
              disabled={
                formInput === null ||
                !formInput.Name ||
                !formInput.OctaneId
              }
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>

        {mutateResult && (
          <SweetAlert
            type={mutateResult.type}
            title={mutateResult.title}
            onConfirm={() => setMutateResult(null)}
            showConfirm={mutateResult.showConfirm}
            timeout={mutateResult.showConfirm ? 0 : 2500}
          >
            {mutateResult.message}
          </SweetAlert>
        )}
      </div>
    </React.Fragment>
  );
};

export default connect(null, { activateAuthLayout })(ManageAgents);
